<template>
  <div
    class="zm-table"
    role="application"
    :class="[{
      'zm-table--fit': fit,
      'zm-table--striped': stripe,
      'zm-table--border': border || isGroup,
      'zm-table--hidden': isHidden,
      'zm-table--group': isGroup,
      'zm-table--fluid-height': maxHeight,
      'zm-table--scrollable-x': layout.scrollX,
      'zm-table--scrollable-y': layout.scrollY,
      'zm-table--enable-row-hover': !store.states.isComplex,
      'zm-table--enable-row-transition': (store.states.data || []).length !== 0 && (store.states.data || []).length < 100,
      'zm-table--adjust-column': adjustColumn
    }, tableSize ? `zm-table--${ tableSize }` : '']"
    @mouseleave="handleMouseLeave"
  >
    <div class="hidden-columns" ref="hiddenColumns">
      <slot></slot>
    </div>
    <template v-if="stickyHeader">
      <zm-sticky
        v-if="showNotFixedHeader"
        enabled
        check-position="top"
        :append-cls="getStickyAppendCls()"
        :max-top="stickyMaxTop"
        :offset="stickyHeader.offset"
        :debounce="stickyHeader.debounce"
        @sticky-change="onStickyChange"
        ref="stickyHeader"
      >
        <div
          v-mousewheel="handleHeaderFooterMousewheel"
          class="zm-table__header-wrapper"
          ref="headerWrapper"
        >
          <table-header
            ref="tableHeader"
            :store="store"
            :border="border"
            :adjust-column="adjustColumn"
            :default-sort="defaultSort"
            :style="{
              width: headWidth
            }"
          >
          </table-header>
        </div>
      </zm-sticky>
    </template>
    <template v-else>
      <div
        v-if="showNotFixedHeader"
        v-mousewheel="handleHeaderFooterMousewheel"
        class="zm-table__header-wrapper"
        ref="headerWrapper"
      >
        <table-header
          ref="tableHeader"
          :store="store"
          :border="border"
          :adjust-column="adjustColumn"
          :default-sort="defaultSort"
          :style="{
            width: headWidth
          }"
        >
        </table-header>
      </div>
    </template>
    <div
      class="zm-table__body-wrapper"
      ref="bodyWrapper"
      :class="[layout.scrollX ? `is-scrolling-${scrollPosition}` : 'is-scrolling-none']"
      :key="tableBodyKey"
    >
      <zm-scrollbar
        ref="bar"
        @scroll="syncPosition"
        :sticky="stickyBar"
        :wrap-style="[bodyHeight]"
      >
        <table-body
          :context="context"
          :store="store"
          :stripe="stripe"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
          :style="{
            width: bodyWidth
          }"
        >
        </table-body>
        <div
          v-if="!data || data.length === 0"
          class="zm-table__empty-block"
          ref="emptyBlock"
          tabindex="0"
        >
          <span class="zm-table__empty-text" role="alert">
            <slot name="empty">
              <span :class="{'zm-table__empty-image': showEmptyImage}">
                {{ emptyText || t('table.emptyText') }}
              </span>
            </slot>
          </span>
        </div>
        <div
          v-if="$slots.append"
          class="zm-table__append-wrapper"
          ref="appendWrapper"
        >
          <slot name="append"></slot>
        </div>
      </zm-scrollbar>
    </div>
    <div
      v-if="showSummary"
      v-show="data && data.length > 0"
      v-mousewheel="handleHeaderFooterMousewheel"
      class="zm-table__footer-wrapper"
      ref="footerWrapper"
    >
      <table-footer
        :store="store"
        :border="border"
        :sum-text="sumText || t('table.sumText')"
        :summary-method="summaryMethod"
        :default-sort="defaultSort"
        :style="{
          width: headWidth
        }"
      />
    </div>
    <div
      v-if="showFixedLeft"
      v-mousewheel="handleFixedMousewheel"
      @mouseenter="onEnterFixedPanel"
      @mouseleave="onLeaveFixedPanel"
      class="zm-table__fixed"
      ref="fixedWrapper"
    >
      <template v-if="stickyHeader">
        <zm-sticky
          enabled
          check-position="top"
          v-if="showHeader"
          :append-cls="getStickyAppendCls('fixed-left')"
          :max-top="stickyMaxTop"
          :offset="stickyHeader.offset"
          :debounce="stickyHeader.debounce"
          ref="leftStickyHeader"
        >
          <div
            class="zm-table__fixed-header-wrapper"
            ref="fixedHeaderWrapper"
          >
            <table-header
              ref="fixedTableHeader"
              fixed="left"
              :border="border"
              :adjust-column="adjustColumn"
              :store="store"
            />
          </div>
        </zm-sticky>
      </template>
      <template v-else>
        <div
          v-if="showHeader"
          class="zm-table__fixed-header-wrapper"
          ref="fixedHeaderWrapper"
        >
          <table-header
            ref="fixedTableHeader"
            fixed="left"
            :border="border"
            :adjust-column="adjustColumn"
            :store="store"
          />
        </div>
      </template>
      <div
        class="zm-table__fixed-body-wrapper"
        ref="fixedBodyWrapper"
      >
        <table-body
          fixed="left"
          :store="store"
          :stripe="stripe"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
        />
        <div
          v-if="$slots.append"
          class="zm-table__append-gutter"
          :style="{ height: layout.appendHeight + 'px'}"
        ></div>
      </div>
      <div
        v-if="showSummary"
        v-show="data && data.length > 0"
        class="zm-table__fixed-footer-wrapper"
        ref="fixedFooterWrapper"
      >
        <table-footer
          fixed="left"
          :border="border"
          :sum-text="sumText || t('table.sumText')"
          :summary-method="summaryMethod"
          :store="store"
          :style="{
            width: bodyWidth
          }"
        />
      </div>
    </div>
    <div
      v-if="showFixedRight"
      v-mousewheel="handleFixedMousewheel"
      @mouseenter="onEnterFixedPanel"
      @mouseleave="onLeaveFixedPanel"
      class="zm-table__fixed-right"
      ref="rightFixedWrapper"
    >
      <template v-if="stickyHeader">
        <zm-sticky
          enabled
          v-if="showHeader"
          check-position="top"
          :append-cls="getStickyAppendCls('fixed-right')"
          :max-top="stickyMaxTop"
          :offset="stickyHeader.offset"
          :debounce="stickyHeader.debounce"
          ref="rightStickyHeader"
        >
          <div
            class="zm-table__fixed-header-wrapper"
            ref="rightFixedHeaderWrapper"
          >
            <table-header
              ref="rightFixedTableHeader"
              fixed="right"
              :border="border"
              :adjust-column="adjustColumn"
              :store="store"
            />
          </div>
        </zm-sticky>
      </template>
      <template v-else>
        <div
          v-if="showHeader"
          class="zm-table__fixed-header-wrapper"
          ref="rightFixedHeaderWrapper"
        >
          <table-header
            ref="rightFixedTableHeader"
            fixed="right"
            :border="border"
            :adjust-column="adjustColumn"
            :store="store"
          />
        </div>
      </template>
      <div
        class="zm-table__fixed-body-wrapper"
        ref="rightFixedBodyWrapper"
      >
        <table-body
          fixed="right"
          :store="store"
          :stripe="stripe"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
        />
        <div
          v-if="$slots.append"
          class="zm-table__append-gutter"
          :style="{ height: layout.appendHeight + 'px' }"
        ></div>
      </div>
      <div
        v-if="showSummary"
        v-show="data && data.length > 0"
        class="zm-table__fixed-footer-wrapper"
        ref="rightFixedFooterWrapper"
      >
        <table-footer
          fixed="right"
          :border="border"
          :sum-text="sumText || t('table.sumText')"
          :summary-method="summaryMethod"
          :store="store"
          :style="{
            width: bodyWidth
          }"
        />
      </div>
    </div>
    <div
      v-if="showFixedRight"
      class="zm-table__fixed-right-patch"
      ref="rightFixedPatch"
      :style="{
        width: '0',
        height: layout.headerHeight + 'px'
      }"
    />
    <div
      class="zm-table__column-resize-proxy"
      ref="resizeProxy"
      v-show="resizeProxyVisible"
      :key="tableResizeKey"
    ></div>
    <template v-if="stickyHeader">
      <zm-sticky
        v-if="initDisplayedColumn && data.length>0"
        enabled
        check-position="top"
        :append-cls="getStickyAppendCls()"
        :sticky-height="layout.headerHeight"
        :z-index="11"
        :style="{position:'static'}"
        :max-top="stickyMaxTop"
        :offset="stickyHeader.offset"
        :debounce="stickyHeader.debounce"
        ref="settingStickyHeader"
      >
        <zm-tooltip
          :disabled="!initDisplayedColumn.title"
          :content="initDisplayedColumn.title"
          placement="top"
        >
          <div
            class="zm-table__column-setting"
            :style="{
              top: settingTop
            }"
          >
            <zm-button
              tabindex="-1"
              @click="showColumnPanel"
              @keydown.native.down.prevent="showColumnPanel"
              type="icon"
              icon="zm-icon-settings"
              class="zm-table__column-setting-btn"
              ref="settingBtn"
              size="mini"
              :aria-label="initDisplayedColumn.title || 'Column setting'"
            />
          </div>
        </zm-tooltip>
      </zm-sticky>
    </template>
    <template v-else>
      <zm-tooltip
        v-if="initDisplayedColumn && data.length>0"
        :disabled="!initDisplayedColumn.title"
        :content="initDisplayedColumn.title"
        placement="top"
      >
        <div
          class="zm-table__column-setting"
          :style="{
            top: settingTop
          }"
        >
          <zm-button
            tabindex="-1"
            @click="showColumnPanel"
            @keydown.native.down.prevent="showColumnPanel"
            type="icon"
            icon="zm-icon-settings"
            class="zm-table__column-setting-btn"
            ref="settingBtn"
            size="mini"
            :aria-label="initDisplayedColumn.title || 'Column setting'"
          />
        </div>
      </zm-tooltip>
    </template>
  </div>
</template>

<script type="text/babel">
  import '../../../i18n/table';
  import ZmButton from '../button';
  import ZmScrollbar from '../scrollbar';
  import ZmSticky from '../sticky';
  import ZmTooltip from '../tooltip';
  import { debounce } from 'throttle-debounce';
  import { addResizeListener, removeResizeListener } from '../../utils/resize-event';
  import Mousewheel from '../../directives/mousewheel';
  import Locale from '../../mixins/locale';
  import { createStore, mapStates } from './store/helper';
  import TableLayout from './table-layout';
  import TableBody from './table-body';
  import TableHeader from './table-header';
  import TableFooter from './table-footer';
  import { parseHeight } from './util';
  import storage from 'good-storage';
  import { _Vue } from './index';
  import ColumnPanel from './column-panel';
  import Grid from './grid';
  import { noop } from '../../utils/util';

  let tableIdSeed = 1;

  const EVENT_LAYOUT = 'layout';

  export default {
    name: 'ZmTable',

    mixins: [Locale],

    directives: {
      Mousewheel
    },

    props: {
      data: {
        type: Array,
        default() {
          return [];
        }
      },

      size: String,

      width: [String, Number],

      height: [String, Number],

      maxHeight: [String, Number],

      fit: {
        type: Boolean,
        default: true
      },

      stripe: Boolean,

      border: Boolean,

      rowKey: [String, Function],

      context: Object,

      showHeader: {
        type: Boolean,
        default: true
      },

      showSummary: Boolean,

      sumText: String,

      summaryMethod: Function,

      rowClassName: [String, Function],

      rowStyle: [Object, Function],

      cellClassName: [String, Function],

      cellStyle: [Object, Function],

      headerRowClassName: [String, Function],

      headerRowStyle: [Object, Function],

      headerCellClassName: [String, Function],

      headerCellStyle: [Object, Function],

      highlightCurrentRow: Boolean,

      currentRowKey: [String, Number],

      emptyText: String,
      showEmptyImage: {
        type: Boolean,
        default: true
      },

      expandRowKeys: Array,

      defaultExpandAll: Boolean,

      defaultSort: Object,

      tooltipEffect: String,

      spanMethod: Function,

      selectOnIndeterminate: {
        type: Boolean,
        default: true
      },

      indent: {
        type: Number,
        default: 16
      },

      treeProps: {
        type: Object,
        default() {
          return {
            hasChildren: 'hasChildren',
            children: 'children'
          };
        }
      },

      lazy: Boolean,

      load: Function,

      minWidth: {
        type: Number,
        default: 500
      },

      initDisplayedColumn: Object,

      stickyBar: Boolean,

      stickyHeader: {
        type: [Boolean, Object],
        default: false
      },

      useFlip: {
        type: Boolean,
        default: true
      },

      forceExpand: {
        type: [Boolean, Object],
        default: false
      },

      ariaLabel: String,

      useMemo: Boolean,

      showHeaderWhenNoData: Boolean,

      determineTooltip: {
        type: String,
        default: ''
      },

      useGrid: {
        type: Boolean,
        default: true
      },

      adjustColumn: [Boolean, Object],

      dataFixedScrollingPosition: Boolean
    },

    components: {
      TableHeader,
      TableFooter,
      TableBody,
      ZmButton,
      ZmScrollbar,
      ZmSticky,
      ZmTooltip
    },

    methods: {
      getBrotherNodeByClass(el, classNames) {
        const parent = el.parentNode;
        const els = Array.from(parent.childNodes);
        return els.find(x => {
          if (x.nodeType !== 1) return false;
          if (!x.className) return false;
          return classNames.every(t => x.className.includes(t));
        });
      },

      updateFixedRowHeight() {
        const bodyEls = this.$el.querySelectorAll('.zm-table__body-wrapper');
        const headerEls = this.isHeaderSticky ? document.querySelectorAll('.zm-sticky-fixed .zm-table__header-wrapper') : this.$el.querySelectorAll('.zm-table__header-wrapper');

        for (let index = 0; index < bodyEls.length; index++) {
          const bodyEl = bodyEls[index];
          const headerEl = headerEls[index];
          const fixedLeftBodyEl = this.getBrotherNodeByClass(bodyEl, ['zm-table__fixed']); // 'zm-table__fixed-body-wrapper'
          const fixedRightBodyEl = this.getBrotherNodeByClass(bodyEl, ['zm-table__fixed-right']); // 'zm-table__fixed-body-wrapper'

          if (!fixedLeftBodyEl && !fixedRightBodyEl) continue;

          // fixed column header height set
          try {
            const fixedLeftHeaderEl = this.isHeaderSticky
              ? document.querySelectorAll('.zm-sticky-fixed.fixed-left .zm-table__fixed-header-wrapper')[index]
              : fixedLeftBodyEl
                ? fixedLeftBodyEl.querySelector('.zm-table__fixed-header-wrapper')
                : undefined;
            const fixedRightHeaderEl = this.isHeaderSticky
              ? document.querySelectorAll('.zm-sticky-fixed.fixed-right .zm-table__fixed-header-wrapper')[index]
              : fixedRightBodyEl
                ? fixedRightBodyEl.querySelector('.zm-table__fixed-header-wrapper')
                : undefined;
            const headerRows = headerEl && headerEl.querySelectorAll('tr');
            const fixedLeftHeaderRows = fixedLeftHeaderEl && fixedLeftHeaderEl.querySelectorAll('tr');
            const fixedRightHeaderRows = fixedRightHeaderEl && fixedRightHeaderEl.querySelectorAll('tr');

            const headerRowCount = headerRows && headerRows.length;

            for (let i = 0; i < headerRowCount; i++) {
              const height = headerRows[i].getBoundingClientRect().height;
              fixedLeftHeaderRows && (fixedLeftHeaderRows[i].style.height = `${height}px`);
              fixedRightHeaderRows && (fixedRightHeaderRows[i].style.height = `${height}px`);
            }
          } catch (error) {
            console.warn('set fixed column header height', error);
          }

          // fixed column body height set
          try {
            const bodyRows = bodyEl && bodyEl.querySelectorAll('tr');
            const fixedLeftBodyRows = fixedLeftBodyEl && fixedLeftBodyEl.querySelector('.zm-table__fixed-body-wrapper').querySelectorAll('tr');
            const fixedRightBodyRows = fixedRightBodyEl && fixedRightBodyEl.querySelector('.zm-table__fixed-body-wrapper').querySelectorAll('tr');

            const bodyRowCount = bodyRows && bodyRows.length;

            for (let i = 0; i < bodyRowCount; i++) {
              const height = bodyRows[i].getBoundingClientRect().height;

              if (fixedLeftBodyRows && fixedLeftBodyRows[i]) {
                fixedLeftBodyRows[i].style.height = `${height}px`;
              } else {
                fixedRightBodyRows && this.setNestedTableFixedHeight(Array.from(bodyRows), Array.from(fixedLeftBodyRows));
              }

              if (fixedRightBodyRows && fixedRightBodyRows[i]) {
                fixedRightBodyRows[i].style.height = `${height}px`;
              } else {
                fixedRightBodyRows && this.setNestedTableFixedHeight(Array.from(bodyRows), Array.from(fixedRightBodyRows));
              }
            }
          } catch (error) {
            console.warn('set fixed column body height', error);
          }

        }
      },

      setNestedTableFixedHeight(bodyRows, fixedBodys) {
        const expandedTableTr = bodyRows.filter(tr => tr.className.includes('zm-table__expanded-tr'));
        const fixedBodysTr = fixedBodys.filter(tr => tr.className.includes('zm-table__expanded-tr--fixed'));

        if (fixedBodysTr.length === 0) return;

        expandedTableTr.forEach((tr, index) => {
          const height = tr.getBoundingClientRect().height;
          if (fixedBodysTr[index] && fixedBodysTr[index].style) {
            fixedBodysTr[index].style.height = `${height}px`;
          }
        });
      },

      makeSureFixedRowHeightRight() {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          this.updateFixedRowHeight();
          if (this.stickyHeader) {
            this.updateSticky();
          }
        }, 1000);
      },

      onStickyChange(isSticky) {
        this.isHeaderSticky = isSticky;
        this.debouncedCreateOrUpdateGrid();
      },

      setCurrentRow(row) {
        this.store.commit('setCurrentRow', row);
      },

      toggleRowSelection(row, selected) {
        this.store.toggleRowSelection(row, selected, false);
        this.store.updateAllSelected();
      },

      toggleRowExpansion(row, expanded) {
        this.store.toggleRowExpansionAdapter(row, expanded);
      },

      clearSelection() {
        this.store.clearSelection();
      },

      clearFilter(columnKeys) {
        this.store.clearFilter(columnKeys);
      },

      clearSort() {
        this.store.clearSort();
      },

      handleMouseLeave() {
        this.store.commit('setHoverRow', null);
        if (this.hoverState) this.hoverState = null;
      },

      updateScrollY() {
        const changed = this.layout.updateScrollY();
        if (changed) {
          this.layout.updateColumnsWidth();
        }
      },

      /* istanbul ignore next */
      handleFixedMousewheel(event, data) {
        if (!this.$refs.bar) {
          return;
        }
        const bodyWrapper = this.$refs.bar.$refs.wrap;
        if (Math.abs(data.spinY) > 0) {
          const currentScrollTop = bodyWrapper.scrollTop;
          if (data.pixelY < 0 && currentScrollTop !== 0) {
            event.preventDefault();
          }
          if (data.pixelY > 0 && bodyWrapper.scrollHeight - bodyWrapper.clientHeight > currentScrollTop) {
            event.preventDefault();
          }
          bodyWrapper.scrollTop += Math.ceil(data.pixelY / 5);
        } else {
          bodyWrapper.scrollLeft += Math.ceil(data.pixelX / 5);
        }
      },

      /* istanbul ignore next */
      handleHeaderFooterMousewheel(event, data) {
        const { pixelX, pixelY } = data;
        if (Math.abs(pixelX) >= Math.abs(pixelY)) {
          this.bodyWrapper.scrollLeft += data.pixelX / 5;
        }
      },

      syncPosition: function({ scrollLeft, scrollTop, offsetWidth, scrollWidth }) {
        const { headerWrapper, footerWrapper, fixedBodyWrapper, rightFixedBodyWrapper } = this.$refs;
        if (headerWrapper) headerWrapper.style.transform = `translate3d(-${scrollLeft}px,0,0)`;
        if (footerWrapper) footerWrapper.style.transform = `translate3d(-${scrollLeft}px,0,0)`;
        if (fixedBodyWrapper) fixedBodyWrapper.style.transform = `translate3d(0,-${scrollTop}px,0)`;
        if (rightFixedBodyWrapper) rightFixedBodyWrapper.style.transform = `translate3d(0,-${scrollTop}px,0)`;
        const { scrollY, gutterWidth } = this.layout;
        const maxScrollLeftPosition = scrollWidth + (scrollY ? gutterWidth : 0) - offsetWidth - 1;
        if (scrollLeft >= maxScrollLeftPosition) {
          this.scrollPosition = 'right';
        } else if (scrollLeft === 0) {
          this.scrollPosition = 'left';
        } else {
          this.scrollPosition = 'middle';
        }
      },

      bindEvents() {
        // this.bodyWrapper.addEventListener('scroll', this.syncPosition, { passive: true });
        if (this.fit) {
          addResizeListener(this.$el, this.resizeListener);
        }
      },

      unbindEvents() {
        // this.bodyWrapper.removeEventListener('scroll', this.syncPosition, { passive: true });
        if (this.fit) {
          removeResizeListener(this.$el, this.resizeListener);
        }
      },

      resizeListener() {
        if (!this.$ready) return;
        let shouldUpdateLayout = false;
        const el = this.$el;
        const { width: oldWidth, height: oldHeight } = this.resizeState;

        const width = el.offsetWidth;
        if (oldWidth !== width) {
          shouldUpdateLayout = true;
        }

        const height = el.offsetHeight;
        if (oldHeight !== height) {
          shouldUpdateLayout = true;
        }

        if (shouldUpdateLayout) {
          this.resizeState.width = width;
          this.resizeState.height = height;
          !this.dataFixedScrollingPosition && this.$refs.bar && this.$refs.bar.scrollToBeginX();
          this.debouncedUpdateLayout();
        }
      },

      doLayout() {
        this.layout.updateColumnsWidth();
        this.$nextTick(() => {
          // if flip update, will headerWrapper DOM change, use nextTick layout updateElsHeight
          this.layout.updateElsHeight();
        });
        clearTimeout(this.updateTimer);
        this.updateTimer = setTimeout(() => {
          if (this.$refs.bar) {
            this.$refs.bar.update();
            !this.dataFixedScrollingPosition && this.$refs.bar.scrollToBeginX();
          }
          this.debouncedCreateOrUpdateGrid();
          this.updateFixedRowHeight();
          if (this.stickyHeader) {
            this.updateSticky();
          }
        }, 20);
        this.$emit(EVENT_LAYOUT, this.layout);
      },

      updateSticky() {
        const stickyRef = ['stickyHeader', 'leftStickyHeader', 'rightStickyHeader', 'settingStickyHeader'];
        stickyRef.forEach((item) => {
          if (this.$refs[item]) {
            this.$refs[item].updateCloneNode();
          }
        });
      },

      sort(prop, order) {
        this.store.commit('sort', { prop, order });
      },

      toggleAllSelection() {
        this.store.commit('toggleAllSelection');
      },

      updateDisplayedColumns() {
        const props = this.displayedProps;
        this.store.states._columns.forEach((column) => {
          if (column.type === 'selection' || column.type === 'expand' || column.type === 'index' || column.fixed) {
            return;
          }
          if (!column.needDisplay && props.indexOf(column.property) > -1) {
            this.store.commit('showColumn', column.property);
          } else if (column.needDisplay && props.indexOf(column.property) === -1) {
            this.store.commit('hideColumn', column.property);
          }
        });
      },

      showColumnPanel() {
        if (!this.columnPanel) {
          this.columnPanel = new _Vue(ColumnPanel);
          this.columnPanel.table = this;
          this.columnPanel.cell = this.$refs.settingBtn.$el;
          this.columnPanel.displayedProps = this.displayedProps.slice();
          this.columnPanel.ignore = this.initDisplayedColumn.ignore;
          this.columnPanel.tip = this.initDisplayedColumn.tip;
          this.columnPanel.count = this.initDisplayedColumn.count;
          this.columnPanel.movable = this.initDisplayedColumn.movable;
          this.columnPanel.searchVisible = this.initDisplayedColumn.searchVisible;
          this.columnPanel.searchPlaceholder = this.initDisplayedColumn.searchPlaceholder;
          this.columnPanel.$mount(document.createElement('div'));
          this.columnPanel.$on('confirm', (displayedProps, movableList) => {
            this.displayedProps = displayedProps;
            if (this.columnPanel.movable) {
              this.movableList = movableList;
            }
          });
        } else {
          this.columnPanel.cell = this.$refs.settingBtn.$el;
          this.columnPanel.displayedProps = this.displayedProps.slice();
          // if (this.columnPanel.movable) {
          //   this.columnPanel.movableList = this.movableList.slice();
          // }
        }

        setTimeout(() => {
          this.columnPanel.showPopper = true;
        }, 17);
      },
      resetColumnPanel() {
        // close column panel when data empty
        if (this.data && this.data.length === 0 && this.columnPanel) {
          this.columnPanel.showPopper = false;
        }
      },
      onEnterFixedPanel() {
        this.$refs.bar && this.$refs.bar.focus();
      },

      onLeaveFixedPanel() {
        this.$refs.bar && this.$refs.bar.blur();
      },

      getStickyAppendCls(other) {
        const cls = ['zm-table'];
        if (other) {
          cls.push(other);
        }
        const stickyHeader = this.stickyHeader;
        if (stickyHeader && stickyHeader.appendCls) {
          cls.push(stickyHeader.appendCls);
        }
        return cls.join(' ');
      },

      setGrid() {
        this.debouncedCreateOrUpdateGrid = this.useGrid ? debounce(50, () => {
          if (!this.grid) {
            this.grid = new Grid(this.$el, this.isHeaderSticky, this.flip);
          } else {
            this.grid.rebuild(this.isHeaderSticky, this.flip);
          }
        }) : noop;
      }
    },

    computed: {
      flip() {
        return this.useFlip && this.store.states.flip;
      },

      stickyMaxTop() {
        return this.layout.tableHeight - this.layout.headerHeight;
      },

      settingTop() {
        return this.store.states.hasSortColumn ? '17px' : '12px';
      },

      tableSize() {
        return this.size || (this.$ELEMENT || {}).size;
      },

      bodyWrapper() {
        return this.$refs.bodyWrapper;
      },

      // shouldUpdateHeight() {
      //   return this.height ||
      //     this.maxHeight ||
      //     this.fixedColumns.length > 0 ||
      //     this.rightFixedColumns.length > 0;
      // },

      headWidth() {
        const { bodyWidth, scrollY, gutterWidth } = this.layout;
        return bodyWidth ? bodyWidth + (scrollY ? gutterWidth : 0) + 'px' : '';
      },

      bodyWidth() {
        const { bodyWidth } = this.layout;
        return bodyWidth ? bodyWidth + 'px' : '';
      },

      bodyHeight() {
        const { headerHeight = 0, bodyHeight, footerHeight = 0, scrollY, gutterWidth } = this.layout;
        if (this.height) {
          return {
            height: this.height === 'auto' ? 'auto' : bodyHeight ? bodyHeight + (scrollY ? gutterWidth : 0) + 'px' : ''
          };
        } else if (this.maxHeight) {
          const maxHeight = parseHeight(this.maxHeight);
          if (typeof maxHeight === 'number') {
            return {
              'max-height': (maxHeight - footerHeight - (this.showHeader ? headerHeight : 0)) + 'px'
            };
          }
        }
        return {};
      },

      // fixedBodyHeight() {
      //   if (this.height) {
      //     return {
      //       height: this.layout.fixedBodyHeight ? this.layout.fixedBodyHeight + 'px' : ''
      //     };
      //   } else if (this.maxHeight) {
      //     let maxHeight = parseHeight(this.maxHeight);
      //     if (typeof maxHeight === 'number') {
      //       // maxHeight = this.layout.scrollX ? maxHeight + 2 * this.layout.gutterWidth : maxHeight;
      //       if (this.showHeader) {
      //         maxHeight -= this.layout.headerHeight;
      //       }
      //       maxHeight -= this.layout.footerHeight;
      //       return {
      //         'max-height': maxHeight + 'px'
      //       };
      //     }
      //   }
      //   return {};
      // },

      // fixedHeight() {
      //   if (this.maxHeight) {
      //     return {
      //       bottom: 0
      //     };
      //   } else {
      //     if (this.showSummary) {
      //       return {
      //         height: this.layout.tableHeight ? this.layout.tableHeight + 'px' : ''
      //       };
      //     }
      //     return {
      //       height: this.layout.viewportHeight ? this.layout.viewportHeight + 'px' : ''
      //     };
      //   }
      // },

      displayedColumnsKey() {
        if (this.initDisplayedColumn) {
          return `zm-table-${this.initDisplayedColumn.key}`;
        }
        return null;
      },

      movableColumnsKey() {
        if (this.initDisplayedColumn.movable) {
          return `zm-table-${this.initDisplayedColumn.key}-movable`;
        }
        return null;
      },

      showFixedRight() {
        return this.rightFixedColumns.length > 0 && !this.flip && this.layout.scrollX && this.data.length > 0;
      },

      showFixedLeft() {
        return this.fixedColumns.length > 0 && !this.flip && this.layout.scrollX && this.data.length > 0;
      },

      showNotFixedHeader() {
        return this.showHeader && !this.flip && (this.showHeaderWhenNoData || this.data.length > 0);
      },

      ...mapStates({
        selection: 'selection',
        columns: 'columns',
        tableData: 'data',
        fixedColumns: 'fixedColumns',
        rightFixedColumns: 'rightFixedColumns'
      })
    },

    provide() {
      return {
        table: this
      };
    },

    watch: {
      height: {
        immediate: true,
        handler(value) {
          this.layout.setHeight(value);
        }
      },

      maxHeight: {
        immediate: true,
        handler(value) {
          this.layout.setMaxHeight(value);
        }
      },

      currentRowKey: {
        immediate: true,
        handler(value) {
          if (!this.rowKey) return;
          this.store.setCurrentRowKey(value);
        }
      },

      data: {
        immediate: true,
        deep: true,
        handler(value) {
          this.store.commit('setData', value);
          if (!value || value.length === 0) {
            this.$refs.bar && this.$refs.bar.scrollToBeginX();
          }
          this.$nextTick(() => {
            this.debouncedUpdateLayout();
            this.makeSureFixedRowHeightRight();
            this.$refs.bar && this.$refs.bar.updateSticky();
            this.setGrid();
          });
        }
      },

      expandRowKeys: {
        immediate: true,
        handler(newVal) {
          if (newVal) {
            this.store.setExpandRowKeysAdapter(newVal);
          }
        }
      }
    },

    created() {
      const seed = tableIdSeed++;
      this.tableId = 'zm-table_' + seed;
      this.tableBodyKey = 'zm-table-body_' + seed;
      this.tableResizeKey = 'zm-table-resize_' + seed;
      this.isHeaderSticky = false;
      this.debouncedUpdateLayout = debounce(50, () => {
        this.doLayout();
        this.resetColumnPanel();
      });
      this.setGrid();
    },

    mounted() {
      this.bindEvents();

      if (this.initDisplayedColumn) {
        let { key, props, movable, useStorage = true } = this.initDisplayedColumn;
        if (!key) {
          throw new Error('You need to specify a unique key for the displayed column.');
        }
        if (useStorage) {
          props = storage.get(this.displayedColumnsKey, props || []);
        }

        this.$watch('displayedProps', (newVal) => {
          if (newVal) {
            storage.set(this.displayedColumnsKey, newVal);
            this.$nextTick(() => {
              this.$refs.bar.scrollToBeginX();
              this.updateDisplayedColumns();
            });
          }
        });

        if (movable) {
          const movableList = storage.get(this.movableColumnsKey, []);

          this.$watch('movableList', (newVal) => {
            storage.set(this.movableColumnsKey, newVal);
            this.$nextTick(() => {
              this.$refs.bar.scrollToBeginX();
              this.store.commit('updateMovableColumn', newVal);
            });
          });

          this.movableList = movableList;
        }

        this.displayedProps = props;
      }

      this.store.updateColumns();
      this.doLayout();

      this.resizeState = {
        width: this.$el.offsetWidth,
        height: this.$el.offsetHeight
      };

      // init filters
      this.store.states.columns.forEach(column => {
        if (column.filteredValue && column.filteredValue.length) {
          this.store.commit('filterChange', {
            column,
            values: column.filteredValue,
            silent: true
          });
        }
      });

      this.$ready = true;
    },

    beforeDestroy() {
      if (this.columnPanel) {
        this.columnPanel.showPopper = false;
      }
    },

    destroyed() {
      clearTimeout(this.timer);
      clearTimeout(this.updateTimer);
      this.unbindEvents();
    },

    data() {
      const { hasChildren = 'hasChildren', children = 'children' } = this.treeProps;
      this.store = createStore(this, {
        rowKey: this.rowKey,
        defaultExpandAll: this.defaultExpandAll || this.forceExpand,
        selectOnIndeterminate: this.selectOnIndeterminate,
        // TreeTable 的相关配置
        indent: this.indent,
        lazy: this.lazy,
        lazyColumnIdentifier: hasChildren,
        childrenColumnName: children
      });
      const layout = new TableLayout({
        store: this.store,
        table: this,
        fit: this.fit,
        showHeader: this.showHeader
      });
      return {
        layout,
        isHidden: false,
        renderExpanded: null,
        resizeProxyVisible: false,
        resizeState: {
          width: null,
          height: null
        },
        displayedProps: [],
        movableList: [],
        // 是否拥有多级表头
        isGroup: false,
        scrollPosition: 'left'
      };
    }
  };
</script>

<style lang="scss">
  @import "./style";
</style>
